import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentRepository = ({ documents, onUploadDocument, onPreviewDocument, onDownloadDocument }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'all', label: 'All Documents', count: documents?.length },
    { id: 'legal', label: 'Legal', count: documents?.filter(d => d?.category === 'legal')?.length },
    { id: 'financial', label: 'Financial', count: documents?.filter(d => d?.category === 'financial')?.length },
    { id: 'technical', label: 'Technical', count: documents?.filter(d => d?.category === 'technical')?.length },
    { id: 'compliance', label: 'Compliance', count: documents?.filter(d => d?.category === 'compliance')?.length }
  ];

  const filteredDocuments = documents?.filter(doc => {
    const matchesCategory = selectedCategory === 'all' || doc?.category === selectedCategory;
    const matchesSearch = doc?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         doc?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'signed': return 'text-success bg-success/10';
      case 'pending-signature': return 'text-warning bg-warning/10';
      case 'uploaded': return 'text-accent bg-accent/10';
      case 'rejected': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return 'FileText';
      case 'doc': case 'docx': return 'FileText';
      case 'jpg': case 'jpeg': case 'png': return 'Image';
      case 'xls': case 'xlsx': return 'FileSpreadsheet';
      default: return 'File';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Upload Button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Document Repository</h3>
        <Button 
          variant="default" 
          iconName="Upload" 
          iconPosition="left"
          onClick={onUploadDocument}
        >
          Upload Document
        </Button>
      </div>
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
          />
        </div>
        
        <div className="flex space-x-2 overflow-x-auto">
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => setSelectedCategory(category?.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-smooth ${
                selectedCategory === category?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <span>{category?.label}</span>
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                selectedCategory === category?.id
                  ? 'bg-primary-foreground/20 text-primary-foreground'
                  : 'bg-background text-muted-foreground'
              }`}>
                {category?.count}
              </span>
            </button>
          ))}
        </div>
      </div>
      {/* Document List */}
      <div className="space-y-3">
        {filteredDocuments?.map((document) => (
          <div key={document?.id} className="bg-card border border-border rounded-lg p-4 hover:shadow-soft transition-smooth">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 flex-1 min-w-0">
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={getFileIcon(document?.type)} size={20} className="text-muted-foreground" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-foreground truncate">{document?.name}</h4>
                    {document?.version > 1 && (
                      <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full">
                        v{document?.version}
                      </span>
                    )}
                    {document?.blockchainVerified && (
                      <Icon name="Shield" size={14} className="text-success" title="Blockchain Verified" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{document?.description}</p>
                  <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                    <span>Uploaded by {document?.uploadedBy}</span>
                    <span>•</span>
                    <span>{document?.uploadDate}</span>
                    <span>•</span>
                    <span>{document?.size}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3 flex-shrink-0">
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(document?.status)}`}>
                  {document?.status === 'signed' && <Icon name="CheckCircle" size={12} className="mr-1" />}
                  {document?.status === 'pending-signature' && <Icon name="Clock" size={12} className="mr-1" />}
                  {document?.status === 'rejected' && <Icon name="XCircle" size={12} className="mr-1" />}
                  {document?.status?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                </div>

                <div className="flex items-center space-x-1">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => onPreviewDocument(document)}
                    title="Preview"
                  >
                    <Icon name="Eye" size={16} />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => onDownloadDocument(document)}
                    title="Download"
                  >
                    <Icon name="Download" size={16} />
                  </Button>
                  {document?.status === 'pending-signature' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      iconName="PenTool"
                      iconPosition="left"
                    >
                      Sign
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" title="More actions">
                    <Icon name="MoreVertical" size={16} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Signature Progress */}
            {document?.requiredSignatures && document?.requiredSignatures?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Signature Progress</span>
                  <span className="text-sm font-medium text-foreground">
                    {document?.completedSignatures}/{document?.requiredSignatures?.length}
                  </span>
                </div>
                <div className="flex space-x-2">
                  {document?.requiredSignatures?.map((signature, index) => (
                    <div 
                      key={index}
                      className={`flex-1 h-2 rounded-full ${
                        signature?.completed ? 'bg-success' : 'bg-muted'
                      }`}
                      title={`${signature?.stakeholder} - ${signature?.completed ? 'Signed' : 'Pending'}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {filteredDocuments?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No documents found</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery ? 'Try adjusting your search terms' : 'Upload your first document to get started'}
          </p>
          <Button variant="outline" iconName="Upload" iconPosition="left" onClick={onUploadDocument}>
            Upload Document
          </Button>
        </div>
      )}
    </div>
  );
};

export default DocumentRepository;